<template>
    <section class="card border-0 shadow-sm mb-4">
        <div class="card-header bg-white border-bottom">
            <template v-if="selfService">
                <h5 class="mb-1">
                    <button
                        type="button"
                        class="btn btn-link p-0 w-100 d-flex align-items-center justify-content-between text-start text-decoration-none"
                        :aria-expanded="isExpanded"
                        :aria-controls="`account-deletion-details-${normalizedPersonId}`"
                        @click="toggleExpanded"
                    >
                        <span>Account deletion and location actions</span>
                        <i class="material-icons ms-2" aria-hidden="true">{{ isExpanded ? "expand_less" : "expand_more" }}</i>
                    </button>
                </h5>
                <p class="text-sm text-danger mb-0">
                    Account-wide deletion removes and disables your account. Proceed only with care; expand to review the scope and assignment impact.
                </p>
            </template>
            <template v-else>
                <h5 class="mb-1">Account deletion and location actions</h5>
                <p class="text-sm text-secondary mb-0">
                    Review linked locations and future assignments before applying an action. Server authorization and assignment state are checked again when you confirm.
                </p>
            </template>
        </div>
        <div
            :id="`account-deletion-details-${normalizedPersonId}`"
            v-show="!selfService || isExpanded"
            class="card-body"
        >
            <div v-if="selfService" class="alert alert-warning mb-4" role="note">
                <strong>Important:</strong> Deleting your account across all linked locations removes and disables your account. Review the selected scope and future assignments carefully; only confirm if you are certain. Active-location-only removal and the other listed actions keep your account.
            </div>
            <div v-if="previewLoading" class="text-muted mb-3" role="status">
                Loading account locations and future assignments...
            </div>

            <div v-if="previewError" class="alert alert-warning" role="alert">
                <p class="mb-2">{{ previewError }}</p>
                <button type="button" class="btn btn-sm btn-outline-dark mb-0" :disabled="previewLoading" @click="loadPreview">
                    Retry preview
                </button>
            </div>

            <div v-if="submitError" class="alert alert-danger" role="alert">
                {{ submitError }}
                <p class="small mb-0 mt-1">Your selected scope, action, and message have been kept.</p>
            </div>

            <div v-if="successMessage" class="alert alert-success" role="status">
                {{ successMessage }}
            </div>

            <template v-if="preview">
                <div class="mb-4">
                    <h6>Linked locations</h6>
                    <ul v-if="locations.length" class="mb-0">
                        <li v-for="location in locations" :key="location.locationid">
                            {{ location.locationname || `Location #${location.locationid}` }}
                            <span v-if="Number(location.locationid) === activeLocationId" class="text-muted">(active location)</span>
                        </li>
                    </ul>
                    <div v-else class="alert alert-warning mb-0" role="alert">
                        No linked locations were returned. Refresh the preview before applying an account action.
                    </div>
                </div>

                <fieldset v-if="!isLocationOnlyResolution" class="mb-4" :disabled="showConfirmation || submitting">
                    <legend class="h6">Apply deletion to</legend>
                    <div v-if="locations.length > 1" class="form-check mb-2">
                        <input
                            :id="`account-scope-location-${normalizedPersonId}`"
                            v-model="deletionScope"
                            class="form-check-input"
                            type="radio"
                            value="location"
                        >
                        <label class="form-check-label" :for="`account-scope-location-${normalizedPersonId}`">
                            Active location only — {{ activeLocationName }}
                        </label>
                    </div>
                    <div class="form-check">
                        <input
                            :id="`account-scope-all-${normalizedPersonId}`"
                            v-model="deletionScope"
                            class="form-check-input"
                            type="radio"
                            value="all"
                            :disabled="!canDeleteAll"
                        >
                        <label class="form-check-label" :for="`account-scope-all-${normalizedPersonId}`">
                            All linked locations ({{ locations.length }})
                        </label>
                    </div>
                    <small v-if="!canDeleteAll" class="form-text text-muted d-block">
                        All-location deletion stays unavailable unless the server confirms access to every linked location.
                    </small>
                    <small v-if="locations.length > 1" class="form-text text-muted">
                        Location-only removal keeps the shared profile and other location memberships. All-location deletion anonymizes and disables the account while preserving schedule history.
                    </small>
                </fieldset>
                <div v-else class="alert alert-info" role="note">
                    This action is always limited to the active location ({{ activeLocationName }}); the account and other location memberships are kept.
                </div>

                <fieldset class="mb-4" :disabled="showConfirmation || submitting">
                    <legend class="h6">Action</legend>
                    <div v-for="option in resolutionOptions" :key="option.value" class="form-check mb-3">
                        <input
                            :id="`account-resolution-${option.value}-${normalizedPersonId}`"
                            v-model="resolution"
                            class="form-check-input"
                            type="radio"
                            :value="option.value"
                        >
                        <label class="form-check-label" :for="`account-resolution-${option.value}-${normalizedPersonId}`">
                            <strong>{{ option.label }}</strong>
                            <span class="d-block small text-muted">{{ option.description }}</span>
                        </label>
                    </div>
                </fieldset>

                <div v-if="resolution === 'notify_and_delete'" class="mb-4">
                    <label :for="`account-deletion-message-${normalizedPersonId}`" class="form-label">
                        Optional message to affected workgroup admins
                    </label>
                    <textarea
                        :id="`account-deletion-message-${normalizedPersonId}`"
                        v-model="message"
                        class="form-control"
                        rows="3"
                        placeholder="Leave blank to use the default message"
                        :disabled="showConfirmation || submitting"
                    />
                    <small class="form-text text-muted">
                        The message is sent only to workgroup admins affected by the assignments in the selected scope.
                    </small>
                </div>

                <div class="mb-4">
                    <h6>Future assignments in scope</h6>
                    <div v-if="!assignmentGroups.length" class="text-muted">
                        No assignments were returned for this scope.
                    </div>
                    <div v-for="location in assignmentGroups" :key="location.locationid" class="mb-3">
                        <strong>{{ location.locationname || `Location #${location.locationid}` }}</strong>
                        <ul v-if="location.assignments.length" class="mb-0 mt-1">
                            <li
                                v-for="assignment in location.assignments"
                                :key="`${location.locationid}-${assignment.ai_workgroup_id}`"
                            >
                                {{ assignment.activity_name || "Assignment" }} — {{ assignment.workgroup_name || "Workgroup" }}
                                <span class="d-block small text-muted">{{ assignment.start_datetime_formatted || "Date unavailable" }}</span>
                            </li>
                        </ul>
                        <p v-else class="small text-muted mb-0 mt-1">No active future assignments at this location.</p>
                    </div>
                </div>

                <div v-if="showConfirmation" class="alert alert-warning mb-3" role="alert">
                    <h6 class="alert-heading">Review and confirm</h6>
                    <p class="mb-1"><strong>User:</strong> {{ displayName }}</p>
                    <p class="mb-1"><strong>Action:</strong> {{ selectedResolutionLabel }}</p>
                    <p class="mb-2"><strong>Scope:</strong> {{ selectedScopeLabel }}</p>
                    <p class="small mb-2">
                        The preview includes {{ assignmentCount }} active future assignment{{ assignmentCount === 1 ? "" : "s" }} in this scope.
                    </p>
                    <div v-if="autoScheduleChoiceRequired" class="border rounded p-3 mb-3">
                        <p class="text-danger mb-2">
                            This removal will create {{ assignmentCount }} vacanc{{ assignmentCount === 1 ? "y" : "ies" }} in future assignment{{ assignmentCount === 1 ? "" : "s" }}.
                        </p>
                        <fieldset>
                            <legend class="h6">Should the auto-scheduler reconsider these vacancies after removal?</legend>
                            <div class="form-check mb-2">
                                <input
                                    :id="`account-autoschedule-yes-${normalizedPersonId}`"
                                    v-model="resetAutoScheduleChoice"
                                    class="form-check-input"
                                    type="radio"
                                    :value="true"
                                    :disabled="submitting"
                                >
                                <label class="form-check-label" :for="`account-autoschedule-yes-${normalizedPersonId}`">
                                    Yes — mark affected Activity Instances for auto-scheduling on the engine's next run.
                                </label>
                            </div>
                            <div class="form-check">
                                <input
                                    :id="`account-autoschedule-no-${normalizedPersonId}`"
                                    v-model="resetAutoScheduleChoice"
                                    class="form-check-input"
                                    type="radio"
                                    :value="false"
                                    :disabled="submitting"
                                >
                                <label class="form-check-label" :for="`account-autoschedule-no-${normalizedPersonId}`">
                                    No — leave the vacancies for manual scheduling.
                                </label>
                            </div>
                        </fieldset>
                    </div>
                    <div class="form-check mb-3">
                        <input
                            :id="`account-confirm-${normalizedPersonId}`"
                            v-model="confirmationChecked"
                            class="form-check-input"
                            type="checkbox"
                            :disabled="submitting"
                        >
                        <label class="form-check-label" :for="`account-confirm-${normalizedPersonId}`">
                            I reviewed the scope and assignment impact and confirm this action.
                        </label>
                    </div>
                    <div class="d-flex justify-content-end gap-2">
                        <button type="button" class="btn btn-outline-secondary mb-0" :disabled="submitting" @click="cancelConfirmation">
                            Back
                        </button>
                        <button
                            type="button"
                            class="btn mb-0"
                            :class="isDeletionResolution ? 'btn-danger' : 'btn-primary'"
                            :disabled="!confirmationChecked || !canSubmit || (autoScheduleChoiceRequired && resetAutoScheduleChoice === null)"
                            @click="confirmAccountAction"
                        >
                            <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            {{ submitting ? "Applying..." : isDeletionResolution ? "Confirm and remove" : "Confirm and apply" }}
                        </button>
                    </div>
                </div>

                <div class="d-flex justify-content-end">
                    <button
                        type="button"
                        class="btn mb-0"
                        :class="isDeletionResolution ? 'btn-danger' : 'btn-outline-primary'"
                        :disabled="!canSubmit || showConfirmation"
                        @click="reviewAccountAction"
                    >
                        {{ isDeletionResolution ? "Review removal" : "Review action" }}
                    </button>
                </div>
            </template>
        </div>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { useStore } from "vuex"

const props = defineProps({
    personId: { type: [Number, String], required: true },
    personName: { type: String, default: "" },
    locationId: { type: [Number, String], required: true },
    selfService: { type: Boolean, default: false },
})
const emit = defineEmits(["completed"])

const store = useStore()
const preview = ref(null)
const previewLoading = ref(false)
const previewError = ref("")
const submitError = ref("")
const successMessage = ref("")
const isExpanded = ref(false)
const deletionScope = ref("location")
const resolution = ref("delete")
const message = ref("")
const showConfirmation = ref(false)
const confirmationChecked = ref(false)
const resetAutoScheduleChoice = ref(false)
const submitting = ref(false)
let previewRequestId = 0

const normalizedPersonId = computed(() => Number(props.personId))
const activeLocationId = computed(() => Number(props.locationId))
const locations = computed(() => preview.value?.locations || [])
const activeLocation = computed(() => locations.value.find(
    (location) => Number(location.locationid) === activeLocationId.value
))
const activeLocationName = computed(() => (
    activeLocation.value?.locationname || `Location #${activeLocationId.value}`
))
const isLocationOnlyResolution = computed(() => (
    resolution.value === "remove_skills" || resolution.value === "unavailable"
))
const isDeletionResolution = computed(() => (
    resolution.value === "delete" || resolution.value === "notify_and_delete"
))
const canDeleteAll = computed(() => preview.value?.can_delete_all === true)
const effectiveScope = computed(() => (
    isLocationOnlyResolution.value ? "location" : deletionScope.value
))
const previewReady = computed(() => (
    !previewLoading.value
    && !previewError.value
    && Array.isArray(preview.value?.locations)
    && preview.value.locations.length > 0
))
const canSubmit = computed(() => (
    previewReady.value
    && !submitting.value
    && Number.isInteger(normalizedPersonId.value)
    && normalizedPersonId.value > 0
    && Number.isInteger(activeLocationId.value)
    && activeLocationId.value > 0
    && (!isDeletionResolution.value || effectiveScope.value !== "all" || canDeleteAll.value)
))
const displayName = computed(() => (
    props.personName || `User #${normalizedPersonId.value}`
))

const allAssignments = computed(() => preview.value?.assignments || [])
const assignmentsInScope = computed(() => {
    if (effectiveScope.value === "location" || !canDeleteAll.value) {
        return allAssignments.value.filter(
            (assignment) => Number(assignment.locationid) === activeLocationId.value
        )
    }
    return allAssignments.value
})
const assignmentGroups = computed(() => {
    const visibleLocations = locations.value.filter((location) => (
        (effectiveScope.value === "all" && canDeleteAll.value)
        || Number(location.locationid) === activeLocationId.value
    ))

    return visibleLocations.map((location) => ({
        ...location,
        assignments: assignmentsInScope.value.filter(
            (assignment) => Number(assignment.locationid) === Number(location.locationid)
        ),
    }))
})
const assignmentCount = computed(() => assignmentsInScope.value.length)
const autoScheduleChoiceRequired = computed(() => (
    !props.selfService
    && isDeletionResolution.value
    && assignmentCount.value > 0
))

const resolutionOptions = [
    {
        value: "delete",
        label: "Decline future assignments and delete",
        description: "Decline future assignments in the selected scope, then remove the membership or account according to the scope.",
    },
    {
        value: "notify_and_delete",
        label: "Notify workgroup admins, decline assignments, and delete",
        description: "Notify only affected workgroup admins, decline future assignments, then remove the membership or account according to the scope.",
    },
    {
        value: "remove_skills",
        label: "Remove assigned and interest skills",
        description: "Remove this user's assigned and interest skills at the active location; keep the account and other location memberships.",
    },
    {
        value: "unavailable",
        label: "Mark unavailable through December 31, 2099",
        description: "Decline active-location future assignments and add a location-scoped unavailable range; keep the account.",
    },
]

const selectedResolutionLabel = computed(() => (
    resolutionOptions.find((option) => option.value === resolution.value)?.label || "Apply account action"
))
const selectedScopeLabel = computed(() => (
    effectiveScope.value === "all" ? "All linked locations" : `Active location only — ${activeLocationName.value}`
))

const getErrorMessage = (error, fallback) => (
    error?.response?.data?.message
    || error?.messages?.join?.("\n")
    || error?.message
    || fallback
)

const loadPreview = async ({ resetScope = false } = {}) => {
    const personId = normalizedPersonId.value
    const locationId = activeLocationId.value
    if (!Number.isInteger(personId) || personId <= 0 || !Number.isInteger(locationId) || locationId <= 0) {
        previewError.value = "A selected user and active location are required to load this preview."
        return
    }

    const requestId = ++previewRequestId
    previewLoading.value = true
    previewError.value = ""
    submitError.value = ""
    try {
        const result = await store.dispatch("fetchAccountDeletionPreview", {
            personId: props.selfService ? undefined : personId,
            locationId,
        })
        if (requestId !== previewRequestId) return

        if (resetScope || result.locations.length <= 1) {
            deletionScope.value = result.locations.length > 1 ? "location" : "all"
        } else if (result.can_delete_all !== true && deletionScope.value === "all") {
            deletionScope.value = "location"
        }
        preview.value = result
    } catch (error) {
        if (requestId !== previewRequestId) return
        previewError.value = getErrorMessage(error, "Could not load account locations and future assignments.")
    } finally {
        if (requestId === previewRequestId) {
            previewLoading.value = false
        }
    }
}

const toggleExpanded = async () => {
    isExpanded.value = !isExpanded.value
    if (isExpanded.value && !preview.value && !previewLoading.value) {
        await loadPreview({ resetScope: true })
    }
}
const reviewAccountAction = () => {
    if (!canSubmit.value) return
    submitError.value = ""
    successMessage.value = ""
    confirmationChecked.value = false
    resetAutoScheduleChoice.value = false
    showConfirmation.value = true
}

const cancelConfirmation = () => {
    if (submitting.value) return
    showConfirmation.value = false
    confirmationChecked.value = false
    resetAutoScheduleChoice.value = false
}

const confirmAccountAction = async () => {
    if (
        !canSubmit.value
        || !confirmationChecked.value
        || !showConfirmation.value
        || (autoScheduleChoiceRequired.value && resetAutoScheduleChoice.value === null)
    ) return

    submitting.value = true
    submitError.value = ""
    try {
        const result = await store.dispatch("submitAccountDeletion", {
            personId: props.selfService ? undefined : normalizedPersonId.value,
            locationId: activeLocationId.value,
            scope: effectiveScope.value,
            resolution: resolution.value,
            message: resolution.value === "notify_and_delete" ? message.value : undefined,
            resetAutoSchedule: autoScheduleChoiceRequired.value
                ? resetAutoScheduleChoice.value
                : undefined,
        })

        if (result?.success !== true || !result?.data) {
            throw new Error(result?.message || "The account action could not be completed.")
        }

        const shouldLeaveEditor = isDeletionResolution.value || !!result.data.account_deleted
        successMessage.value = result.data.message || "The account action completed successfully."
        showConfirmation.value = false
        confirmationChecked.value = false
        resetAutoScheduleChoice.value = false
        emit("completed", {
            data: result.data,
            scope: effectiveScope.value,
            resolution: resolution.value,
            shouldLeaveEditor,
        })

        if (!shouldLeaveEditor) {
            await loadPreview()
        }
    } catch (error) {
        submitError.value = getErrorMessage(error, "Could not apply this account action. Your choices have been kept.")
        if (error?.response?.status === 409) {
            previewError.value = "Assignments or location scope changed after the preview. Reload the preview before confirming again."
        }
        showConfirmation.value = false
        confirmationChecked.value = false
        resetAutoScheduleChoice.value = false
    } finally {
        submitting.value = false
    }
}

watch(
    () => [props.personId, props.locationId],
    async () => {
        previewRequestId++
        preview.value = null
        previewError.value = ""
        submitError.value = ""
        successMessage.value = ""
        deletionScope.value = "location"
        resolution.value = "delete"
        message.value = ""
        showConfirmation.value = false
        confirmationChecked.value = false
        resetAutoScheduleChoice.value = false
        if (!props.selfService || isExpanded.value) {
            await loadPreview({ resetScope: true })
        }
    },
    { immediate: true }
)

onBeforeUnmount(() => {
    previewRequestId++
})
</script>
